import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getClientsList} from '../../api/UserManagementService';
import {
  SelectedClientState,
  setClientData,
} from '../../redux/slices/selectedClientSlice';
import {RootState} from '../../redux/store';
import AurClientDropdown from '../client-management/aur-client-dropdown/AurClientDropdown';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {showErrorToast} from '../../api/utils';
import {resetPermissionsByProject} from '../../redux/slices/projectPermissionsSlice';
import {t} from 'i18next';

export const CLIENT_DROP_DOWN_ID = 'Clients';
export const CLIENT_SEARCH_ENABLE_COUNT = 25;
const CLIENT_PAGE_SIZE = 25;

interface Props {
  routeName: string;
  sortColumn?: string;
  buttonLabel?: string;
  allowAutoComplete?: boolean;
  onClientSelection?: () => void;
}

const AurClientDropDownWrapper: React.FC<Props> = ({
  routeName,
  sortColumn = '',
  buttonLabel = '',
  onClientSelection,
  allowAutoComplete = true,
}) => {
  const {showToast} = useToast();
  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );

  const [userClients, setUserClients] = useState<SelectedClientState[]>([]);
  const [userClientsLoading, setUserClientsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [clientSearchEnable, setClientSearchEnable] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingInitialClients, setIsLoadingInitialClients] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (
      (routeName === '/reports' ||
        routeName === '/welcome-single-client' ||
        routeName === '/index-search' ||
        routeName === '/users/exportdetails' ||
        routeName === '/reports/invoices' ||
        routeName === '/business-reports') &&
      userClients.length === 0
    ) {
      fetchClientList();
      setIsLoadingInitialClients(true);
    }
  }, [routeName]);

  const onClientPick = useCallback(
    (clientId: string) => {
      const selectedClientData = userClients.find(
        (item) => item.clientId === clientId
      );
      if (selectedClientData) {
        if (clientId != selectedClient?.clientId) {
          dispatch(resetPermissionsByProject());
          dispatch(setClientData(selectedClientData));
        }
        onClientSelection?.();
      }
    },
    [userClients, selectedClient]
  );

  const handleClientSearchInput = useCallback(
    (input: string) => {
      setPageNumber(1); // Reset pagination on new search
      fetchClientList(input, 1, true); // Fresh fetch with search input
    },
    [selectedClient]
  );

  // Define useRef for tracking previous searchInput and page
  const prevInput = useRef<{
    searchInput: string | undefined;
    page: number;
    reset: boolean;
  }>({searchInput: undefined, page: 1, reset: false});

  const fetchClientList = async (
    searchInput?: string,
    page = 1,
    reset = false
  ) => {
    const normalizedInput = searchInput || undefined;
    // Prevent duplicate requests when nothing has changed
    if (
      prevInput.current.searchInput === normalizedInput &&
      prevInput.current.page === page &&
      prevInput.current.reset === reset
    ) {
      return;
    }

    // Update the ref to keep track of the previous values
    prevInput.current = {searchInput: normalizedInput, page, reset};

    if (page === 1) {
      setUserClientsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const sortColumnQueryParam = sortColumn
        ? `&sortColumn=${sortColumn}`
        : '';
      let queryParam = `pageSize=${CLIENT_PAGE_SIZE}&pageNumber=${page}${sortColumnQueryParam}`;
      if (searchInput) queryParam += `&SearchTerm=${searchInput}`;
      const result = await getClientsList(queryParam);

      if (result.data.data) {
        const fetchedClients = result.data.data;
        setHasMore(fetchedClients.length === CLIENT_PAGE_SIZE);

        const newData = reset
          ? [...fetchedClients]
          : [...userClients, ...fetchedClients];

        if (
          userClients.length === 0 &&
          fetchedClients.length >= CLIENT_SEARCH_ENABLE_COUNT &&
          !clientSearchEnable
        ) {
          setClientSearchEnable(true);
        }

        if (!searchInput && page === 1) {
          const selectedOptionExitIndex = newData.findIndex(
            (item: SelectedClientState) =>
              item.clientId === selectedClient.clientId
          );
          if (
            selectedOptionExitIndex === -1 &&
            selectedClient.clientId !== ''
          ) {
            newData.unshift(selectedClient);
            if (newData.length > CLIENT_SEARCH_ENABLE_COUNT) {
              newData.pop();
            }
          }
        }

        setUserClients(newData);
        setIsLoadingInitialClients(false);
      }
    } catch (e) {
      showErrorToast(showToast, e);
    } finally {
      setUserClientsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreClients = () => {
    if (!hasMore || isLoadingMore || isLoadingInitialClients) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchClientList(undefined, nextPage);
  };

  return (
    <span className="header-client-dropdown">
      <AurClientDropdown
        buttonOrTextField="button"
        dropDownID={CLIENT_DROP_DOWN_ID}
        idKey="clientId"
        displayKey="name"
        data={userClients}
        initialSelectedOptionId={
          selectedClient.clientId !== '' ? selectedClient.clientId : undefined
        }
        onOptionPick={onClientPick}
        onSearchInput={handleClientSearchInput}
        searchReqLoading={userClientsLoading}
        disableDropDownForSingleItem
        enableSearch={clientSearchEnable}
        redirectOnItemChange={
          routeName === '/business-reports' ? '' : '/welcome-single-client'
        }
        onLoadMore={loadMoreClients}
        isLoadingMore={isLoadingMore}
        isSortedAlphabetically={false}
        selectDefaultFirstItem={routeName !== '/business-reports'}
        buttonLabel={buttonLabel}
        initialLoading={userClientsLoading}
        ariaLabel={t('accessibility.clientDropdown.client.buttonWithValueHint')}
        allowAutoComplete={allowAutoComplete}
      />
    </span>
  );
};

export default AurClientDropDownWrapper;
